import { Component, OnInit, Input, OnDestroy, ViewChild, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { Tariff, PaymentTypes, Currency, Commission } from '../tariff';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { TariffService } from '../services/tariff.service';
import { RegularPayments } from '../_models/payments';
import { Subscription } from 'rxjs';
import { RegularPaymentsService } from '../services/regular-payments.service';
import { CustomTranslationService } from '@app/shared';

interface ToggleObj {
  is_commission_enabled: boolean;
  is_commission_offer_enabled: boolean;
  use_bonus_value: boolean;
  should_block_ether: boolean;
  should_penalty_offer_cancel: boolean;
  should_penalty_offer_reject: boolean;
  should_penalty_trip_cancel: boolean;
  should_penalty_card_payment_trip_cancel: boolean;
  is_commission_intercity_enabled: boolean;
  is_commission_hidden_mode_enabled: boolean;
  is_commission_hidden_mode_sending_enabled: boolean;
}

@Component({
  selector: 'app-tariff-form',
  templateUrl: './tariff-form.component.html',
  styleUrls: ['./tariff-form.component.scss']
})
export class TariffFormComponent implements OnInit, OnDestroy, AfterViewChecked {
  tariffId;
  @ViewChild('tabGroup') tabGroup;

  baseObject;
  isPaymenformValid = false;
  isScaledValid = false;
  private paymentFormStateSubs: Subscription;
  private scalesValidationSubs: Subscription;
  private addEventSubs: Subscription;

  commissionForm: FormGroup;
  currencyForm: {
    basicCommission: Currency;
    distribution: Currency;
    intercity: Currency;
    penaltyTripCancel: Currency;
    penaltyCardTripCancel: Currency;
    penaltyOfferCancel: Currency;
    penaltyOfferReject: Currency;
    hiddenMode: Currency;
    hiddenSendingMode: Currency;
  };

  toggles: ToggleObj;
  regularPaymentsOfTariff: RegularPayments[];
  offers: RegularPayments[];
  commissionFormTracker: Subscription;
  isCommissionFormValid = false;
  commissionsData: Commission[];
  tariffCommission: Commission[];
  isHiddenClearAndSaveBtns = false;
  selectedTabIndex: number;

  constructor(
    private fb: FormBuilder,
    public tariffService: TariffService,
    private regularPaymentService: RegularPaymentsService,
    private cdRef: ChangeDetectorRef,
    private customTranslationService: CustomTranslationService
  ) {}

  ngOnInit() {
    this.addEventSubs = this.tariffService.addTariffEvent.subscribe(isAdd => {
      this.tabGroup.selectedIndex = 0;
    });
    this.paymentFormStateSubs = this.regularPaymentService.validationRagularPaymentsState.subscribe(
      (isValid: boolean) => {
        this.isPaymenformValid = isValid;
      }
    );
    this.scalesValidationSubs = this.regularPaymentService.validationScalesSate.subscribe((isValid: boolean) => {
      this.isScaledValid = isValid;
    });
    this.commissionFormTracker = this.tariffService.commissionFormsArray.subscribe((formData: Commission[]) => {
      if (formData) {
        this.tariffService.getCommissionData(formData);
        this.isCommissionFormValid = false;
      } else {
        this.isCommissionFormValid = true;
      }
    });
  }

  ngAfterViewChecked() {
    if (this.tabGroup.selectedIndex === 5 || this.tabGroup.selectedIndex === 6 || this.tabGroup.selectedIndex === 7) {
      this.isHiddenClearAndSaveBtns = true;
      this.cdRef.detectChanges();
    } else {
      this.isHiddenClearAndSaveBtns = false;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.commissionFormTracker.unsubscribe();
    this.paymentFormStateSubs.unsubscribe();
    this.scalesValidationSubs.unsubscribe();
    this.addEventSubs.unsubscribe();
  }

  @Input('tariff')
  set tariff(tariff: Tariff) {
    this.baseObject = tariff;
    this.tariffId = tariff.id;
    this.commissionForm = this.fb.group({
      name: [tariff.name, Validators.required],
      recommended_balance: [tariff.recommended_balance],
      bonus_value: [tariff.bonus_value, this.currencyValidator],
      bonus_offer_value: [tariff.bonus_offer_value],
      ether_min_balance: [tariff.ether_min_balance],
      debt_limit: [-1 * tariff.debt_limit, [Validators.min(-10000), Validators.max(10000)]],
      commission_scale_value_type: [tariff.commission_scale_value_type],
      commission_scale_type: [tariff.commission_scale_type],
      commission_scale_request_type: [tariff.commission_scale_request_type],
      commission_scale_request_visibility: [tariff.commission_scale_request_visibility],
      is_arrival_bonus_enabled: [tariff.is_arrival_bonus_enabled],
      arrival_bonus_min_distance: [tariff.arrival_bonus_min_distance],
      arrival_bonus_max_distance: [tariff.arrival_bonus_max_distance],
      arrival_bonus_value: [tariff.arrival_bonus_value],
      arrival_bonus_base_value: [tariff.arrival_bonus_base_value],
      arrival_bonus_queue_type: [tariff.arrival_bonus_queue_type]
    });

    this.toggles = this.generateTogglesObj(tariff);

    this.disableBlock({ checked: tariff.should_block_ether });
    this.disableBonusValue({ checked: tariff.use_bonus_value });

    this.currencyForm = {
      basicCommission: {
        value: tariff.commission_value,
        type: tariff.commission_type,
        isValid: true
      },
      distribution: {
        value: tariff.commission_offer_value,
        type: tariff.commission_offer_type,
        isValid: true
      },
      intercity: {
        value: tariff.commission_intercity_value,
        type: tariff.commission_intercity_type,
        isValid: true
      },
      penaltyTripCancel: {
        value: tariff.penalty_trip_cancel_value,
        type: tariff.penalty_trip_cancel_type,
        isValid: true
      },
      penaltyCardTripCancel: {
        value: tariff.penalty_card_payment_trip_cancel_value || 0,
        type: tariff.penalty_card_payment_trip_cancel_type || PaymentTypes.natural,
        isValid: true
      },
      penaltyOfferCancel: {
        value: tariff.penalty_offer_cancel_value,
        type: tariff.penalty_offer_cancel_type,
        isValid: true
      },
      penaltyOfferReject: {
        value: tariff.penalty_offer_reject_value,
        type: tariff.penalty_offer_reject_type,
        isValid: true
      },
      hiddenMode: {
        value: tariff.commission_hidden_mode_value,
        type: tariff.commission_hidden_mode_type,
        isValid: true
      },
      hiddenSendingMode: {
        value: tariff.commission_hidden_mode_sending_value,
        type: tariff.commission_hidden_mode_sending_type,
        isValid: true
      }
    };
    this.regularPaymentsOfTariff = (tariff.regular_payments || []).filter(payment => payment.operation == 'sub');
    this.offers = (tariff.regular_payments || []).filter(payment => payment.operation == 'add');
    tariff.commissions && tariff.commissions.length
      ? (this.tariffCommission = tariff.commissions)
      : (this.tariffCommission = null);
  }

  currencyValidator(control: AbstractControl) {
    let num = Number(control.value);
    if (!isNaN(num) && num >= 0 && num <= 100) {
      return null;
    } else {
      control.setErrors({ invalid_value_error: true });
    }
  }

  toggleChange(event, field) {
    this.toggles[field] = event.checked;
  }

  disableBonusValue($event) {
    this.toggles.use_bonus_value = $event.checked;
    let func = $event.checked ? 'enable' : 'disable';
    this.commissionForm.get('bonus_value')[func]();
    this.commissionForm.get('bonus_offer_value')[func]();
  }

  disableBlock(event) {
    this.toggles.should_block_ether = event.checked;
    let func = event.checked ? 'enable' : 'disable';
    this.commissionForm.get('ether_min_balance')[func]();
  }

  clear() {
    this.tariff = this.baseObject;
    this.tariffService.clearEvent(true);
  }

  saveCommissionFormChanges() {
    const commissionForm = this.commissionForm.value;
    const toggles = this.toggles;

    const newTariff: Tariff = {
      ...commissionForm,
      ...toggles,
      debt_limit: this.commissionForm.get('debt_limit').value * -1,
      is_default: this.baseObject.is_default
    };

    newTariff.commission_type = this.currencyForm.basicCommission.type;
    newTariff.commission_value = this.currencyForm.basicCommission.value;
    newTariff.commission_offer_type = this.currencyForm.distribution.type;
    newTariff.commission_offer_value = this.currencyForm.distribution.value;
    newTariff.commission_intercity_type = this.currencyForm.intercity.type;
    newTariff.commission_intercity_value = this.currencyForm.intercity.value;
    newTariff.penalty_trip_cancel_value = this.currencyForm.penaltyTripCancel.value;
    newTariff.penalty_trip_cancel_type = this.currencyForm.penaltyTripCancel.type;
    newTariff.penalty_card_payment_trip_cancel_value = this.currencyForm.penaltyCardTripCancel.value;
    newTariff.penalty_card_payment_trip_cancel_type = this.currencyForm.penaltyCardTripCancel.type;
    newTariff.penalty_offer_cancel_value = this.currencyForm.penaltyOfferCancel.value;
    newTariff.penalty_offer_cancel_type = this.currencyForm.penaltyOfferCancel.type;
    newTariff.penalty_offer_reject_value = this.currencyForm.penaltyOfferReject.value;
    newTariff.penalty_offer_reject_type = this.currencyForm.penaltyOfferReject.type;
    newTariff.commission_hidden_mode_value = this.currencyForm.hiddenMode.value;
    newTariff.commission_hidden_mode_type = this.currencyForm.hiddenMode.type;
    newTariff.commission_hidden_mode_sending_value = this.currencyForm.hiddenSendingMode.value;
    newTariff.commission_hidden_mode_sending_type = this.currencyForm.hiddenSendingMode.type;

    for (let key in this.baseObject) {
      if (newTariff[key] == undefined) {
        newTariff[key] = 0;
      }
    }

    if (this.tariffId) {
      newTariff.id = this.tariffId;
      this.tariffService.updateTariff(newTariff);
    } else {
      this.tariffService.createTariff(newTariff);
    }
    this.tariffService.emittCurrentTariff(newTariff);
  }

  setFormValueField(newControlValue: string, controlName: string): void {
    this.commissionForm.controls[controlName].patchValue(newControlValue);
    this.commissionForm.markAsTouched();
  }

  selectedIndexChange(event): void {
    this.selectedTabIndex = event;
  }

  private generateTogglesObj(tariff: Tariff): ToggleObj {
    return {
      is_commission_enabled: tariff.is_commission_enabled,
      is_commission_offer_enabled: tariff.is_commission_offer_enabled,
      use_bonus_value: tariff.use_bonus_value,
      should_block_ether: tariff.should_block_ether,
      should_penalty_offer_cancel: tariff.should_penalty_offer_cancel,
      should_penalty_offer_reject: tariff.should_penalty_offer_reject,
      should_penalty_trip_cancel: tariff.should_penalty_trip_cancel,
      should_penalty_card_payment_trip_cancel: tariff.should_penalty_card_payment_trip_cancel,
      is_commission_intercity_enabled: tariff.is_commission_intercity_enabled,
      is_commission_hidden_mode_enabled: tariff.is_commission_hidden_mode_enabled,
      is_commission_hidden_mode_sending_enabled: tariff.is_commission_hidden_mode_sending_enabled
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/driver-tariffs/tariff-form/tariff-form.component.ts